import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock, Save, Trash2, X, Zap, MessageCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import PhotoPicker from "@/components/PhotoPicker";
import AvatarPicker from "@/components/AvatarPicker";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import PriceTypePicker from "@/components/PriceTypePicker";
import { CATEGORIES, findCategory } from "@/lib/categories";
import { forgetMyAd, getMyAdAuth, getSavedUserEmail, rememberMyAd } from "@/lib/myAds";

export default function ManageAdPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";
  const storedAuth = getMyAdAuth(id);
  const initialToken = tokenFromUrl || storedAuth?.token || "";

  const [ad, setAd] = useState(null);
  const [auth, setAuth] = useState({ token: initialToken, contact_email: "", owner_password: "" });
  const [needsPw, setNeedsPw] = useState(!initialToken);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");
  const action = searchParams.get("action");

  const verify = async (authBody) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/listings/${id}/verify`, authBody);
      const manageToken = data.listing.manage_token || authBody.token || "";
      // Prefer token session (persisted) over keeping password in React state.
      const nextAuth = manageToken
        ? { token: manageToken }
        : {
            contact_email: authBody.contact_email || "",
            owner_password: authBody.owner_password || "",
          };
      setAd(data.listing);
      setForm({
        title: data.listing.title || "",
        description: data.listing.description || "",
        category: data.listing.category,
        subcategory: data.listing.subcategory || "",
        price: data.listing.price ?? "",
        price_type: data.listing.price_type || (data.listing.price_negotiable ? "negotiable" : "fixed"),
        price_negotiable: data.listing.price_negotiable || false,
        location: data.listing.location || "",
        serial_number: data.listing.serial_number || "",
        ean_code: data.listing.ean_code || "",
        vin_code: data.listing.vin_code || "",
        year_of_manufacture: data.listing.year_of_manufacture ?? "",
        machine_hours: data.listing.machine_hours ?? "",
        weight_kg: data.listing.weight_kg ?? "",
        photos: data.listing.photos || [],
        contact_name: data.listing.contact_name || "",
        contact_phone: data.listing.contact_phone || "",
        owner_avatar: data.listing.owner_avatar || null,
      });
      setNeedsPw(false);
      setAuth(nextAuth);
      if (manageToken) {
        rememberMyAd(id, {
          manage_token: manageToken,
          contact_email: data.listing.owner_email || authBody.contact_email,
        });
      }
    } catch (err) {
      // Invalid/expired stored token → forget and show unlock.
      if (authBody?.token && !authBody?.owner_password) {
        forgetMyAd(id);
      }
      setError(apiError(err));
      setNeedsPw(true);
      setAd(null);
      setForm(null);
      setAuth({ token: "", contact_email: "", owner_password: "" });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const authToTry = tokenFromUrl
      ? { token: tokenFromUrl }
      : getMyAdAuth(id);
    if (authToTry?.token) verify(authToTry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl, id]);

  const currentCat = useMemo(
    () => (form?.category ? findCategory(form.category) : null),
    [form?.category]
  );

  if (needsPw) {
    return <UnlockScreen id={id} onVerify={verify} busy={busy} error={error} action={action} />;
  }
  if (!ad || !form) {
    return (
      <Layout>
        <div className="stb-card p-6" data-testid="manage-loading">Načítám…</div>
      </Layout>
    );
  }

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSavedMsg("");
    try {
      const isMachine = form.category === "stroje-tezka-technika";
      const showYear = isMachine || form.subcategory === "elektricke-aku-naradi";
      const toNum = (v) => (v === "" || v == null ? null : Number(v));
      const changes = {
        title: form.title,
        description: form.description,
        category: form.category,
        subcategory: form.subcategory || null,
        price: form.price_type === "fixed" && form.price !== "" ? Number(form.price) : null,
        price_type: form.price_type,
        location: form.location || null,
        serial_number: form.serial_number || null,
        ean_code: form.ean_code || null,
        vin_code: isMachine ? form.vin_code || null : null,
        year_of_manufacture: showYear ? toNum(form.year_of_manufacture) : null,
        machine_hours: isMachine ? toNum(form.machine_hours) : null,
        weight_kg: isMachine ? toNum(form.weight_kg) : null,
        photos: form.photos,
        contact_name: form.contact_name,
        contact_phone: form.contact_phone || null,
        owner_avatar: form.owner_avatar,
      };
      const { data } = await api.post(`/listings/${id}/update`, { auth, changes });
      setAd(data.listing);
      setForm((f) => ({
        ...f,
        photos: data.listing.photos || [],
        owner_avatar: data.listing.owner_avatar || null,
      }));
      setSavedMsg("Uloženo.");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Opravdu smazat tento inzerát?")) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/listings/${id}/delete`, { auth });
      forgetMyAd(id);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
      setBusy(false);
    }
  };

  const lockDevice = () => {
    forgetMyAd(id);
    setAd(null);
    setForm(null);
    setAuth({ token: "", contact_email: "", owner_password: "" });
    setNeedsPw(true);
    setError("");
    setSavedMsg("");
    // Drop token from URL so refresh doesn't re-unlock.
    if (tokenFromUrl) {
      navigate(`/sprava-inzeratu/${id}`, { replace: true });
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <h1 className="stb-h text-3xl text-[#0E0E0E]">Správa inzerátu</h1>
        <p className="text-sm text-[#0E0E0E] mt-1">{ad.title}</p>

        {action === "top" && (
          <div className="stb-card p-3 mt-3 border-[#FF6B00]" data-testid="action-top-banner">
            <p className="stb-h text-[#FF6B00] text-sm inline-flex items-center gap-2">
              <Zap size={14} /> Klikni níže na &bdquo;TOP za 49 Kč&ldquo; – inzerát se posune nahoru na 14 dní.
            </p>
          </div>
        )}

        <form onSubmit={save} className="stb-card p-5 mt-4 space-y-4" data-testid="manage-form">
          <Field label="Název inzerátu *">
            <input className="stb-input" value={form.title} onChange={(e) => updateField("title", e.target.value)} required minLength={3} maxLength={120} />
          </Field>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Kategorie">
              <select className="stb-input" value={form.category}
                onChange={(e) => { updateField("category", e.target.value); updateField("subcategory", ""); }}>
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Podkategorie">
              <select className="stb-input" value={form.subcategory}
                onChange={(e) => updateField("subcategory", e.target.value)}>
                <option value="">— vyber —</option>
                {currentCat?.subcategories.map((sc) => (
                  <option key={sc.slug} value={sc.slug}>{sc.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Popis *">
            <textarea className="stb-input" value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              required minLength={10} maxLength={8000} />
          </Field>

          <div>
            <h3 className="stb-label mb-2">Cena *</h3>
            <PriceTypePicker
              value={form.price_type || (form.price_negotiable ? "negotiable" : "fixed")}
              onChange={(v) => { updateField("price_type", v); if (v !== "fixed") updateField("price", ""); }}
              testIdPrefix="manage-price-type"
            />
            {(form.price_type || (form.price_negotiable ? "negotiable" : "fixed")) === "fixed" && (
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                <Field label="Částka v Kč *">
                  <input className="stb-input" type="number" min={0} value={form.price}
                    onChange={(e) => updateField("price", e.target.value)} required />
                </Field>
              </div>
            )}
          </div>

          <Field label="Lokalita *">
            <LocationAutocomplete
              value={form.location}
              onChange={(v) => updateField("location", v)}
              required
              testId="manage-location-input"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Výrobní číslo"><input className="stb-input" value={form.serial_number}
              onChange={(e) => updateField("serial_number", e.target.value)} /></Field>
            <Field label="EAN kód"><input className="stb-input" value={form.ean_code}
              onChange={(e) => updateField("ean_code", e.target.value)} /></Field>
          </div>

          {form.category === "stroje-tezka-technika" && (
            <>
              <Field label="VIN kód">
                <input className="stb-input" value={form.vin_code}
                  onChange={(e) => updateField("vin_code", e.target.value)} maxLength={40} />
              </Field>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Rok výroby"><input className="stb-input" type="number" min={1900} max={2100}
                  value={form.year_of_manufacture}
                  onChange={(e) => updateField("year_of_manufacture", e.target.value)} /></Field>
                <Field label="Motohodiny"><input className="stb-input" type="number" min={0}
                  value={form.machine_hours}
                  onChange={(e) => updateField("machine_hours", e.target.value)} /></Field>
                <Field label="Hmotnost (kg)"><input className="stb-input" type="number" min={0}
                  value={form.weight_kg}
                  onChange={(e) => updateField("weight_kg", e.target.value)} /></Field>
              </div>
            </>
          )}

          {form.category !== "stroje-tezka-technika" && form.subcategory === "elektricke-aku-naradi" && (
            <Field label="Rok výroby">
              <input className="stb-input" type="number" min={1900} max={2100}
                value={form.year_of_manufacture}
                onChange={(e) => updateField("year_of_manufacture", e.target.value)} />
            </Field>
          )}

          <Field label="Fotografie">
            <PhotoPicker photos={form.photos} onChange={(p) => updateField("photos", p)} testId="manage-photos" />
          </Field>

          <hr className="stb-divider" />
          <Field label="Profilová fotka (volitelné)">
            <AvatarPicker value={form.owner_avatar} onChange={(v) => updateField("owner_avatar", v)} />
          </Field>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Jméno / přezdívka"><input className="stb-input" value={form.contact_name}
              onChange={(e) => updateField("contact_name", e.target.value)} /></Field>
            <Field label="Telefon (skrytý)"><input className="stb-input" value={form.contact_phone}
              onChange={(e) => updateField("contact_phone", e.target.value)} /></Field>
          </div>

          {error && <p className="text-[#FF6B00] text-sm" data-testid="manage-error">{error}</p>}
          {savedMsg && <p className="text-[#22C55E] text-sm" data-testid="manage-saved">{savedMsg}</p>}

          <div className="flex gap-2 flex-wrap">
            <button type="submit" className="stb-btn stb-btn-primary" disabled={busy} data-testid="manage-save">
              <Save size={14} /> {busy ? "Ukládám…" : "Uložit změny"}
            </button>
            <TopButton ad={ad} auth={auth} />
            <button
              type="button"
              onClick={() => navigate(`/sprava-inzeratu/${id}/zpravy`, { state: { auth } })}
              className="stb-btn stb-btn-secondary"
              data-testid="manage-messages-btn"
            >
              <MessageCircle size={14} /> Zprávy ({ad?.chat_count || 0})
            </button>
            <button type="button" onClick={remove} className="stb-btn stb-btn-secondary" disabled={busy} data-testid="manage-delete">
              <Trash2 size={14} /> Smazat inzerát
            </button>
            <button type="button" onClick={() => navigate(`/inzerat/${id}`)} className="stb-btn stb-btn-ghost">
              <X size={14} /> Otevřít inzerát
            </button>
            <button
              type="button"
              onClick={lockDevice}
              className="stb-btn stb-btn-ghost"
              data-testid="manage-forget-device"
            >
              <Lock size={14} /> Odhlásit na tomto zařízení
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function TopButton({ ad, auth }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/top/checkout", {
        listing_id: ad.id,
        origin,
        auth,
      });
      window.location.href = data.url;
    } catch (e) {
      alert(apiError(e));
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="stb-btn stb-btn-primary"
      data-testid="manage-top-btn"
    >
      <Zap size={14} /> {busy ? "Otevírám…" : "TOP za 49 Kč (14 dní)"}
    </button>
  );
}

function Field({ label, children }) {
  // Use <div>, not <label>: wrapping PhotoPicker/AvatarPicker (file inputs +
  // buttons) in a label makes mobile browsers swallow or double-fire the picker
  // gesture — listing photo uploads never reached POST /api/uploads.
  return (
    <div className="block">
      <span className="stb-label">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function UnlockScreen({ id, onVerify, busy, error, action }) {
  const [email, setEmail] = useState(() => getSavedUserEmail());
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    onVerify({ contact_email: email, owner_password: pw });
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto">
        {action === "top" && (
          <div className="stb-card p-3 mb-3 border-[#FF6B00]" data-testid="action-top-banner">
            <p className="stb-h text-[#FF6B00] text-sm inline-flex items-center gap-2">
              <Zap size={14} /> Pro TOP platbu (49 Kč / 14 dní) se nejprve ověř e-mailem a heslem k inzerátu.
            </p>
          </div>
        )}
        <div className="stb-card p-6" data-testid="manage-unlock">
        <h1 className="stb-h text-2xl text-white inline-flex items-center gap-2">
          <Lock size={20} className="text-[#FF6B00]" /> Správa inzerátu
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Zadej e-mail a heslo, které jsi použil/a při vložení inzerátu.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input className="stb-input" type="email" placeholder="Tvůj e-mail" required
            value={email} onChange={(e) => setEmail(e.target.value)} data-testid="unlock-email" />
          <div className="relative">
            <input className="stb-input pr-12" type={showPw ? "text" : "password"}
              placeholder="Heslo k inzerátu" required value={pw}
              onChange={(e) => setPw(e.target.value)} data-testid="unlock-password" />
            <button type="button" onClick={() => setShowPw((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#FF6B00]" tabIndex={-1}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {error && <p className="text-[#FF6B00] text-sm" data-testid="unlock-error">{error}</p>}
          <button className="stb-btn stb-btn-primary w-full" disabled={busy} data-testid="unlock-submit">
            {busy ? "Ověřuji…" : "Odemknout"}
          </button>
        </form>
        <hr className="stb-divider" />
        <p className="text-xs text-[#A3A3A3]">
          Ztratil/a jsi e-mail i heslo?{" "}
          <a href="/moje-inzeraty-email" className="stb-link">Najdi své inzeráty podle e-mailu</a>.
        </p>
        </div>
      </div>
    </Layout>
  );
}
